import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Send, Loader, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactInfo = [
  { icon: MapPin, label: "Nuestras Oficinas", value: "Laprida 4506 - Caseros, Buenos Aires, Argentina" },
  { icon: Phone, label: "Central Telefónica", value: "0800 122 0402 / +54 372 533" },
  { icon: Clock, label: "Horario de Atención", value: "Lunes a Viernes, 8:00 - 17:00 hs" },
];

export const Contact = () => {
  const [form, setForm] = useState({ nombre: "", email: "", motivo: "", mensaje: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      setError("Por favor completá los campos requeridos (Nombre, Email y Mensaje).");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setTimeout(() => {
        window.open(res.data.whatsapp_url, "_blank");
      }, 500);
    } catch (err) {
      setError("Hubo un error al enviar el mensaje. Intentá de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        backgroundImage: `url(/assets/ELEMENTO1.png)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="absolute inset-0 bg-[#1A1A2E]/40 lg:bg-gradient-to-r lg:from-[#1A1A2E]/95 lg:via-[#1A1A2E]/80 lg:to-[#1A1A2E]/40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-accent font-heading font-semibold text-sm tracking-widest uppercase block mb-3">
            Hablemos
          </span>
          <h2 className="font-heading font-black text-white text-4xl md:text-5xl mb-4">
            Contactanos
          </h2>
          <p className="font-body text-white/60 text-lg">
            Estamos listos para ayudarte con tu proyecto
          </p>
          <div className="w-20 h-1 bg-accent mx-auto mt-6 rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: contact info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            {contactInfo.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} data-testid={`contact-info-${item.label.toLowerCase().replace(/ /g, "-")}`} className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-accent/20 border border-accent/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon size={22} className="text-accent" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-white text-base mb-1">{item.label}</div>
                    <div className="font-body text-white/65 text-sm leading-relaxed">{item.value}</div>
                  </div>
                </div>
              );
            })}

            <div className="mt-8 p-6 bg-white/5 rounded-2xl border border-white/10">
              <p className="font-body text-white/70 text-sm leading-relaxed">
                Completá el formulario y te redireccionaremos a WhatsApp con tu mensaje listo para enviarnos. Respondemos en el día.
              </p>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
              <div className="bg-accent rounded-xl px-6 py-4 mb-8 -mx-2">
                <h3 className="font-heading font-bold text-white text-xl">Dejanos un Mensaje</h3>
                <p className="font-body text-white/80 text-sm mt-1">Te responderemos por WhatsApp</p>
              </div>

              {success ? (
                <div data-testid="contact-success" className="text-center py-8">
                  <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
                  <h4 className="font-heading font-bold text-primary text-xl mb-2">¡Mensaje enviado!</h4>
                  <p className="font-body text-gray-500 text-sm">Se abrirá WhatsApp con tu mensaje. Si no se abrió, revisá tu bloqueador de popups.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="font-heading font-semibold text-sm text-gray-700 mb-2 block">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        data-testid="contact-nombre"
                        type="text"
                        name="nombre"
                        value={form.nombre}
                        onChange={handleChange}
                        placeholder="Tu nombre"
                        className="w-full border-0 border-b-2 border-gray-200 focus:border-accent pb-2 pt-1 text-sm font-body text-gray-800 placeholder:text-gray-400 outline-none bg-transparent transition-colors duration-300"
                      />
                    </div>
                    <div>
                      <label className="font-heading font-semibold text-sm text-gray-700 mb-2 block">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="tu@email.com"
                        className="w-full border-0 border-b-2 border-gray-200 focus:border-accent pb-2 pt-1 text-sm font-body text-gray-800 placeholder:text-gray-400 outline-none bg-transparent transition-colors duration-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-heading font-semibold text-sm text-gray-700 mb-2 block">Motivo</label>
                    <input
                      data-testid="contact-motivo"
                      type="text"
                      name="motivo"
                      value={form.motivo}
                      onChange={handleChange}
                      placeholder="¿En qué podemos ayudarte?"
                      className="w-full border-0 border-b-2 border-gray-200 focus:border-accent pb-2 pt-1 text-sm font-body text-gray-800 placeholder:text-gray-400 outline-none bg-transparent transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="font-heading font-semibold text-sm text-gray-700 mb-2 block">
                      Mensaje <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      data-testid="contact-mensaje"
                      name="mensaje"
                      value={form.mensaje}
                      onChange={handleChange}
                      placeholder="Contanos tu consulta..."
                      rows={4}
                      className="w-full border-0 border-b-2 border-gray-200 focus:border-accent pb-2 pt-1 text-sm font-body text-gray-800 placeholder:text-gray-400 outline-none bg-transparent resize-none transition-colors duration-300"
                    />
                  </div>

                  {error && (
                    <p data-testid="contact-error" className="text-red-500 text-sm font-body bg-red-50 px-4 py-2 rounded-lg">
                      {error}
                    </p>
                  )}

                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-accent text-white py-4 rounded-full font-heading font-bold text-base flex items-center justify-center gap-2 hover:bg-[#0099D6] hover:shadow-[0_0_25px_rgba(0,174,239,0.5)] transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <><Loader size={18} className="animate-spin" /> Enviando...</>
                    ) : (
                      <><Send size={18} /> ENVIAR MENSAJE</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
